import { createPortal } from 'react-dom'
import React, { useState, useEffect, useRef, useCallback } from 'react'
import { get } from 'lodash'
import { Classes, Position, Menu, MenuItem } from '@blueprintjs/core'
import { Popover } from '@ui'
import { Flex, IconButton, Toaster, UsageTypeSelect } from '@ui'
import SpaceAssignmentSelect from './SpaceAssignmentSelect'
import { UseFloorHook, useFloor } from '@providers/mapProvider'
import useSettings from 'components/NewSettings/hooks/useSettings'
import api, {
  IUsageType,
  IFloorMapSpace,
  ISpaceAssignment,
  IEmployee,
  ISpace,
  ICurrentUser,
} from '@api'
import { ApolloError } from 'apollo-boost'
import EmployeeShareDialog from './spaceShareDialog'
import SeatableSpaceCard, { BookingNoteCard } from './Cards/SeatableSpaceCard'
import {
  bookingRestricted,
  isFinder,
  isAdmin,
  DateUtil,
  TIME,
  getDefaultTimes,
  isPrimary,
  isSecondary,
} from '@utils'
import Card from './Cards/Card'
import { AssetsModal, getDepartmentInfo, getEmployeesInfo } from './Cards/SpaceDetailCard'
import css from './Cards/seatableSpaceCard.module.scss'
import { EmployeeDetailCard } from './Card'
import { getContactInfo } from './Cards/EmployeeCard/EmployeeCard'
import SelfMoveButton from './Cards/SelfMoveButton'
import { hotelingDesk, meetingRoom } from '@ui/canvas/utils'
import { useUser } from '@hooks'
import { useAppData, useBookingDesk } from '@providers'
import { selfMoveEnabled } from '@utils/product'
import { ISpaceDetails, ISpaceType, Role } from '@api/types'
import { FinderMeetingRoomCardUnconnected } from './Cards/MeetingRoomCard/MeetingRoomCard'
import { Button } from '@runeffective/evo-design-system'
import useBookingModal from 'components/BookingsModal/hooks/useBookingModal'
import BookingCalendarPopup from 'components/BookingCalendarPopup/BookingCalendarPopup'
import { useBookingCalendar } from 'components/BookingCalendarPopup/hooks'
import { handleGraphQLErrors } from '@utils/error'

interface SelectionPopoverProps {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  canvasMap: any
  floor: UseFloorHook
}

interface IMapSelectionRect {
  x: number
  y: number
  w: number
  h: number
}

export default function SelectionPopover({ canvasMap, floor }: SelectionPopoverProps) {
  const LEFT_OFFSET = 200
  const TOP_OFFSET = 100
  const PADDING = 5
  const toolbarRef = useRef<HTMLDivElement | null>(null)
  const [toolbarRect, setToolbarRect] = useState<ClientRect | null>(null)
  const [mapSelectionRect, setMapSelectionRect] = useState<IMapSelectionRect | null>(null)
  const { innerWidth: w, innerHeight: h } = window
  const user = useUser()
  const floorDetails = useFloor()
  const [selectedEmployee, setBookingSelectedEmployee] = useState(null as IEmployee | null)
  const { fetchBookings } = useBookingModal()
  const { bookDesk, recurrence, bookRecurringDesk, date, updateBookingDate } = useBookingDesk()
  const spaceSelect = floor.selection
  const updateToolbar = useCallback(() => {
    const selectionRect = canvasMap.getSelectionRect()
    setMapSelectionRect(
      selectionRect
        ? {
            ...selectionRect,
            x: LEFT_OFFSET + selectionRect.x,
            y: TOP_OFFSET + selectionRect.y,
          }
        : null
    )
  }, [LEFT_OFFSET, TOP_OFFSET, canvasMap])

  useEffect(() => {
    canvasMap.subscribe('selection', updateToolbar)
    canvasMap.subscribe('onZoom', updateToolbar)

    return () => {
      canvasMap.unsubscribe('selection', updateToolbar)
      canvasMap.unsubscribe('onZoom', updateToolbar)
    }
  }, [canvasMap, updateToolbar])

  function updateToolbarRect() {
    if (toolbarRef.current) {
      const rect = toolbarRef.current.getBoundingClientRect()

      if (!toolbarRect || toolbarRect.width !== rect.width || toolbarRect.height !== rect.height) {
        setToolbarRect(rect)
      }
    }
  }

  useEffect(updateToolbarRect)

  if (
    !mapSelectionRect ||
    (!(floor.isBookMeetingRoomPage || floor.isBookDeskPage) && !isFinder(user?.user?.role))
  )
    return null

  const toolbarW = get(toolbarRect, 'width', 0)
  const toolbarH = get(toolbarRect, 'height', 0)

  const left =
    mapSelectionRect.x + toolbarW + PADDING > w
      ? w - toolbarW - PADDING
      : Math.max(mapSelectionRect.x, LEFT_OFFSET + PADDING)
  const top =
    mapSelectionRect.y > h
      ? h - toolbarH - PADDING
      : Math.max(mapSelectionRect.y - toolbarH - PADDING, TOP_OFFSET + PADDING)

  let space: IFloorMapSpace | undefined
  let spaces: IFloorMapSpace[] | undefined

  if (spaceSelect?.count === 1) {
    space = floor.spaces.byId[Object.keys(spaceSelect?.items || [])[0]]
  } else if (spaceSelect?.count) {
    spaces = Object.keys(spaceSelect?.items || []).map((id) => floor.spaces.byId[id])
  }
  const toggleModal = () => {
    spaceSelect?.clear()
  }
  return createPortal(
    <div
      ref={toolbarRef}
      className={Classes.POPOVER}
      style={{
        padding: 10,
        position: 'absolute',
        background: 'white',
        top,
        left,
      }}>
      {space && floor.isBookDeskPage && (
        <SingleSpaceCard
          floorId={floor.floorId as string}
          assignmentPlanId={floor.apId}
          space={space}
          clear={spaceSelect?.clear}
          onChange={(spaces: IFloorMapSpace[]) => floor.updateSpaces?.(spaces)}
        />
      )}
      {spaces && !isFinder(user?.user?.role) && floor.isBookDeskPage && (
        <MultiSpaceCard
          floorId={floor.floorId as string}
          assignmentPlanId={floor.apId}
          spaces={spaces}
          onChange={(spaces: IFloorMapSpace[]) => floor.updateSpaces?.(spaces)}
        />
      )}
      {meetingRoom(space?.spaceType) &&
        !isFinder(user?.user?.role) &&
        floor.isBookMeetingRoomPage && (
          <FinderMeetingRoomCardUnconnected
            data={{
              spaceType: space?.spaceType,
              id: space?.id,
              name: space?.name,
              tags: space?.tags,
              capacity: space?.capacity,
              bookings: space?.bookings,
              ...space,
            }}
            toggleModal={() => toggleModal()}
          />
        )}
      {space && isFinder(user?.user?.role) && !floor.isBookDeskPage && (
        <FinderSpaceCard
          floorId={floor.floorId as string}
          assignmentPlanId={floor.apId}
          space={space}
          clear={spaceSelect?.clear}
          onChange={(spaces: IFloorMapSpace[]) => floor.updateSpaces?.(spaces)}
          floor={floorDetails}
          bookingObj={{
            selectedEmployee: selectedEmployee,
            setBookingSelectedEmployee: setBookingSelectedEmployee,
            fetchBookings: fetchBookings,
            bookDesk: bookDesk,
            date: date,
            updateBookingDate: updateBookingDate,
          }}
        />
      )}
    </div>,
    document.body
  )
}

function FinderSpaceCard({
  floorId,
  assignmentPlanId,
  space,
  clear = () => {},
  onChange,
  floor,
  bookingObj,
}: {
  floorId: string
  assignmentPlanId: string
  clear?: () => void
  space: ISpace
  onChange: (spaces: IFloorMapSpace[]) => void
  floor: UseFloorHook
  bookingObj: any
}) {
  const toggleModal = () => {
    clear()
  }

  return (
    <>
      <div style={{ position: 'absolute', right: 5 }}>
        <IconButton
          style={{ marginBottom: 8 }}
          round
          minimal
          small
          iconName="times"
          onClick={toggleModal}
        />
      </div>
      <Flex align="center" justify="space-between">
        <Popover
          isOpen={!!space}
          usePortal={false}
          modifiers={{ arrow: { enabled: false } }}
          position={Position.RIGHT_TOP}>
          <FinderDetailCard
            space={space}
            smallText={space.spaceAssignment.department?.name}
            showEmployees
            showWpg
            hideDept
            showAsset
            showAssetModal
            floor={floor}
            bookingObj={bookingObj}
          />
        </Popover>
      </Flex>
    </>
  )
}

const FinderDetailCard = ({
  space,
  showEmployees,
  showEmail,
  showWpg,
  hideDept,
  smallText,
  showAsset,
  showAssetModal,
  floor,
  bookingObj,
  clear = () => {},
}: {
  space: ISpace
  showEmployees?: boolean
  showEmail?: boolean
  showWpg?: boolean
  hideDept?: boolean
  smallText?: string
  showAsset?: boolean
  showAssetModal?: boolean
  floor: UseFloorHook
  bookingObj: any
  clear?: () => void
}) => {
  const settings = useSettings()

  const {
    hotelingDeskDeptEnforcementEnabled,
    hotelingDeskWpgEnforcementEnabled,
    hotelingDeskWpgEnforcementOperator,
  } = settings
  const [bookingNote, setBookingNote] = useState('')
  const [showAssetsModal, setShowAssetsModal] = useState(false)
  const assets = (space.tags || []).map((t) => t.name).sort()
  const assetsList = assets.length ? assets.join(', ') : 'N/A'
  const { user, reload } = useUser()
  const meta = useAppData()
  const selfMove = selfMoveEnabled(meta.product)
  const isMeetingRoom = (spaceType: ISpaceType | null) => spaceType?.name.match(/meeting_/)
  const deskType = isMeetingRoom(space.spaceType)
    ? 'Meeting Room'
    : space.usageType
    ? get(space, 'usageType.displayName', '')
    : 'Other'
  if (deskType === 'Other' && space.globalMapObject) {
    return <Card>{space.globalMapObject.name}</Card>
  }
  const { role = Role.VIEWER } = user || { employee: {} }
  const isAdminRole = isAdmin(role)
  let empList: string[] = []
  const {
    externalId,
    code,
    workplaceGroup,
    spaceAssignment: { department, team, employee, sharedEmployees },
  } = space
  const dept = getDepartmentInfo(department, team)
  const wpg = get(workplaceGroup, 'name', 'N/A')
  const contactInfo = getContactInfo({ phone: employee?.phone, email: employee?.email })
  const isAdminPage = window.location.pathname.includes('/ap')
  const restrictedSettingsObj = {
    hotelingDeskDeptEnforcementEnabled: hotelingDeskDeptEnforcementEnabled,
    hotelingDeskWpgEnforcementEnabled: hotelingDeskWpgEnforcementEnabled,
    hotelingDeskWpgEnforcementOperator: hotelingDeskWpgEnforcementOperator,
  }
  const restrictions =
    !isAdminRole && bookingRestricted({ user, space, settings: restrictedSettingsObj, deskType })

  const bookingSuccessfullyCreated = () => {
    Toaster.success({
      message: 'Booking successfully created',
    })
    floor.selection?.clear()
    employeeId === user?.employee.id && bookingObj.fetchBookings()
    floor.reloadMap?.()
  }
  const employeeId = bookingObj.selectedEmployee
    ? bookingObj.selectedEmployee.id
    : user?.employee.id || ''
  const { id, bookings = [] } = space
  const timezone = get(floor, 'building.timezone', DateUtil.defaultTz())
  const startTime = DateUtil.tz(timezone)
  const endTime = startTime.clone().add(1, TIME.HOURS)
  const defaultDates = getDefaultTimes(
    floor.bookingWorkdayTimeFrom || '',
    floor.bookingWorkdayTimeTo || '',
    timezone
  )
  const correctStartTime = startTime.isSameOrAfter(defaultDates.timeFrom, TIME.HOURS)
  const correctEndTime = endTime.isSameOrBefore(defaultDates.timeTo, TIME.HOURS)
  let hasBookingsToday = 0
  const toggleModal = () => {
    floor?.selection?.clear()
  }
  const canBookNextHour = bookings
    .filter((booking) => {
      // elim unnecessary dates
      const bookingStart = DateUtil.tz(booking.startDate, timezone)
      const bookingEnd = DateUtil.tz(booking.endDate, timezone)

      return (
        bookingStart.isSame(startTime, TIME.DAYS) ||
        (bookingEnd.isSame(endTime, TIME.DAYS) && !booking.autoReleasedAt)
      )
    })
    .reduce((result, current) => {
      if (!result) return result // term early if conflict already found

      const bookingStart = DateUtil.tz(current.startDate, timezone)
      const bookingEnd = DateUtil.tz(current.endDate, timezone)

      return (
        result &&
        !startTime.isBetween(bookingStart, bookingEnd) &&
        !endTime.isBetween(bookingStart, bookingEnd)
      )
    }, true)

  if (employee) {
    empList = [employee.name]
  } else if (sharedEmployees && sharedEmployees.length) {
    empList = getEmployeesInfo(sharedEmployees)
  }
  const capacity = (space: ISpaceDetails | null) => space?.capacity

  const MAX_CHARACTERS_FOR_NOTE = 250
  const isNotBookDesk = true

  return (
    <Card>
      <AssetsModal show={showAssetsModal} onClose={() => setShowAssetsModal(false)} list={assets} />
      <Card.Details>
        {!meetingRoom(space.spaceType) && (
          <Card.Title>
            <span className={css.title}>{deskType}</span>
            <div style={{ fontSize: 14 }}>{smallText}</div>
          </Card.Title>
        )}
        {!hideDept && !meetingRoom(space.spaceType) && (
          <Card.Row label="Department:" value={dept} />
        )}
        {!meetingRoom(space.spaceType) &&
          (isPrimary(space.usageType) || isSecondary(space.usageType)) && (
            <Card.Row
              label="Title:"
              value={employee?.title ? (employee?.title as string) : 'N/A'}
            />
          )}
        {!meetingRoom(space.spaceType) && <Card.Row label="Space Id:" value={externalId} />}
        {!meetingRoom(space.spaceType) && <Card.Row label="Contact:" value={contactInfo} />}
        {showEmployees && empList.length > 0 && (
          <Card.Row
            label="Employees:"
            value={[
              <div style={{ maxWidth: 192 }}>
                {empList.map((e) => (
                  <EmployeeDetailCard showEmail={showEmail} name={e} />
                ))}
              </div>,
            ]}
          />
        )}
        {showWpg && !meetingRoom(space.spaceType) && (
          <Card.Row label="Workplace group:" value={wpg} />
        )}
        {showAsset && !meetingRoom(space.spaceType) && (
          <Card.Row
            label="Assets:"
            value={
              showAssetModal && assets.length > 1
                ? [
                    <div className={css.assetsLink} onClick={() => setShowAssetsModal(true)}>
                      LIST
                    </div>,
                  ]
                : assetsList
            }
          />
        )}
        {meetingRoom(space.spaceType) && (
          <FinderMeetingRoomCardUnconnected
            data={{
              spaceType: space.spaceType,
              id: space.id,
              name: space.name,
              tags: space.tags,
              capacity: space.capacity,
              bookings: space.bookings,
            }}
            toggleModal={toggleModal}
          />
        )}
        {!hotelingDesk(space.usageType) && (
          <SelfMoveButton space={space} reload={reload} user={user} selfmoveOn={selfMove} />
        )}
        {restrictions
          ? hotelingDesk(space.usageType) && (
              <Card.Title>
                <div className={css.restriction} style={{ display: 'block', marginTop: '5px' }}>
                  {<p>{restrictions}</p>}
                </div>
              </Card.Title>
            )
          : !meetingRoom(space.spaceType) &&
            hotelingDesk(space.usageType) && (
              <React.Fragment>
                {settings.bookingNotesEnabled && (
                  <BookingNoteCard
                    bookingNote={bookingNote}
                    setBookingNote={setBookingNote}
                    maxCharLength={MAX_CHARACTERS_FOR_NOTE}
                  />
                )}
                <span className={css.buttonContainer}>
                  <Flex>
                    <Button
                      size="small"
                      layout="primary"
                      style={{ display: 'inline', marginRight: '7px' }}
                      onClick={() => {
                        bookingObj
                          .bookDesk({
                            spaceId: space.id,
                            employeeId,
                            note: bookingNote,
                            startDate: startTime,
                            endDate: endTime,
                          })
                          .then(() => {
                            bookingSuccessfullyCreated()
                          })
                      }}
                      disabled={
                        bookingNote.length > MAX_CHARACTERS_FOR_NOTE ||
                        (settings.hourlyBookingEnabled
                          ? !(canBookNextHour && correctEndTime && correctStartTime)
                          : hasBookingsToday > 0 ||
                            !DateUtil.tz(timezone).isBetween(
                              defaultDates.timeFrom,
                              defaultDates.timeTo
                            ))
                      }>
                      {settings.hourlyBookingEnabled ? 'Book 1 Hour' : 'Book Today'}
                    </Button>
                    <>
                      <div>
                        {
                          <div>
                            <div>
                              <BookingCalendarPopup
                                isNotBookDesk={isNotBookDesk}
                                title="Book Desk"
                                timezone={timezone}
                                updateHandler={(dates) => {
                                  bookingObj.updateBookingDate({
                                    fromDate: dates[0],
                                    toDate: dates[1] || dates[0],
                                  })
                                }}
                                bookDeskHandler={(dates) => {
                                  bookingObj
                                    .bookDesk({
                                      spaceId: space.id,
                                      employeeId,
                                      note: bookingNote,
                                      startDate: dates[0],
                                      endDate: dates[1] || dates[0],
                                    })
                                    .then(() => {
                                      bookingSuccessfullyCreated()
                                    })
                                    .catch(handleGraphQLErrors)
                                }}>
                                <div>
                                  {settings.bookingNotesEnabled && (
                                    <BookingNoteCard
                                      bookingNote={bookingNote}
                                      setBookingNote={setBookingNote}
                                      maxCharLength={MAX_CHARACTERS_FOR_NOTE}
                                    />
                                  )}
                                </div>
                              </BookingCalendarPopup>
                            </div>
                          </div>
                        }
                      </div>
                    </>
                  </Flex>
                </span>
              </React.Fragment>
            )}
      </Card.Details>
    </Card>
  )
}

function SingleSpaceCard({
  floorId,
  assignmentPlanId,
  space,
  clear = () => {},
  onChange,
}: {
  floorId: string
  assignmentPlanId: string
  clear?: () => void
  space: IFloorMapSpace
  onChange: (spaces: IFloorMapSpace[]) => void
}) {
  // if (!seatable(space.spaceType)) {
  //   return <div>TODO TEST</div>
  // }
  const toggleModal = () => {
    clear()
  }
  return (
    <>
      <Flex align="center" justify="space-between">
        {/* <Flex align="center">
          {space && (
            <div
              style={{ fontWeight: 'bold', marginRight: 10, cursor: 'pointer' }}
              onClick={() => navigate(`?spaceId=${space.id}`)}>
              {space.code}
            </div>
          )}
          {space && <UsageTypeTag usageType={space.usageType} />}
        </Flex> */}
        <Popover
          isOpen={!!space}
          usePortal={false}
          modifiers={{ arrow: { enabled: false } }}
          position={Position.RIGHT_TOP}>
          <SeatableSpaceCard toggleModal={toggleModal} space={space} />
        </Popover>
      </Flex>
      {/* <div style={{ marginTop: 10 }}>
        <SpaceAssignmentSelect {...{ floorId, assignmentPlanId, space, onChange }} />
      </div> */}
    </>
  )
}

function MultiSpaceCard({
  floorId,
  assignmentPlanId,
  spaces,
  onChange,
}: {
  floorId: string
  assignmentPlanId: string
  spaces: IFloorMapSpace[]
  onChange: (spaces: IFloorMapSpace[]) => void
}) {
  const [isSharePopoverOpen, setIsSharePopoverOpen] = useState(false)
  const [employeeList, setemployeeList] = useState<IEmployee[]>([])
  function updateSpaceUsage(usageType: IUsageType) {
    const nextSpaces = spaces.map((s) => {
      const spaceAssignment = s.spaceAssignment as ISpaceAssignment
      return {
        ...s,
        usageType,
        spaceAssignment: {
          ...spaceAssignment,
          employee: null,
          neighborhood: null,
        },
      }
    })

    if (nextSpaces.length) {
      onChange(nextSpaces)

      api.space
        .batchUpdateUsageTypes(
          nextSpaces.map(({ id }) => id),
          usageType.name
        )
        .then(() => {
          Toaster.success({
            message:
              nextSpaces.length === 1
                ? `${nextSpaces[0].code} changed to ${usageType.displayName}`
                : `${nextSpaces.length} spaces changed to ${usageType.displayName}`,
          })
        })
        .catch((error: ApolloError) => {
          Toaster.fail({ error })
        })
    }
  }
  return (
    <div>
      <div>{spaces.length} spaces selected</div>
      <Flex style={{ marginTop: 10 }}>
        <SpaceAssignmentSelect {...{ floorId, assignmentPlanId, spaces, onChange }} />
        <div style={{ margin: 5 }} />
        <UsageTypeSelect btnText="Update usage" value={null} onChange={updateSpaceUsage} />
        <div style={{ margin: '0 10px' }} />
        <Popover
          usePortal={false}
          modifiers={{ arrow: { enabled: false } }}
          position={Position.RIGHT_TOP}
          content={
            <Menu>
              <MenuItem text="Assets" />
              <MenuItem text="Space type" />
            </Menu>
          }>
          <IconButton minimal iconName="ellipsis-h" tooltip="More actions" style={{ width: 32 }} />
        </Popover>
        <EmployeeShareDialog
          spaces={spaces}
          assignmentPlanId={assignmentPlanId}
          setIsSharePopoverOpen={setIsSharePopoverOpen}
          isSharePopoverOpen={isSharePopoverOpen}
          employeeList={employeeList}
          setemployeeList={setemployeeList}
        />
      </Flex>
    </div>
  )
}
